import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { Bell, X, ArrowRight, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * In-app "you haven't finished today" nudge.
 * Shows if:
 *   - user has reminder_enabled = true AND a reminder_time set
 *   - current local time >= reminder_time
 *   - at least one task remaining in today's day
 *   - not already dismissed today (localStorage per YYYY-MM-DD)
 * Re-checks every 60s.
 */
export default function ReminderBanner() {
  const { user } = useAuth();
  const [show, setShow] = useState(false);
  const [remaining, setRemaining] = useState(0);

  const evaluate = useCallback(async () => {
    if (!user?.reminder_enabled || !user?.reminder_time) { setShow(false); return; }
    const today = new Date().toLocaleDateString("en-CA"); // YYYY-MM-DD in local tz
    const dismissKey = `adyap:reminder-dismissed:${today}`;
    if (localStorage.getItem(dismissKey)) { setShow(false); return; }
    // is current local time past the reminder_time?
    const [rh, rm] = user.reminder_time.split(":").map((v) => parseInt(v, 10));
    if (Number.isNaN(rh) || Number.isNaN(rm)) { setShow(false); return; }
    const now = new Date();
    const nowMins = now.getHours() * 60 + now.getMinutes();
    const remMins = rh * 60 + rm;
    if (nowMins < remMins) { setShow(false); return; }
    // any tasks left today?
    try {
      const { data } = await api.get("/tasks/today");
      const tasks = data?.day?.tasks || [];
      const left = tasks.filter((t) => !t.completed).length;
      setRemaining(left);
      setShow(left > 0);
    } catch {
      setShow(false);
    }
  }, [user?.reminder_enabled, user?.reminder_time]);

  useEffect(() => {
    evaluate();
    const id = setInterval(evaluate, 60_000);
    const onSwitch = () => evaluate();
    window.addEventListener("adyap:goal-switched", onSwitch);
    return () => { clearInterval(id); window.removeEventListener("adyap:goal-switched", onSwitch); };
  }, [evaluate]);

  if (!show) return null;

  const dismiss = () => {
    const today = new Date().toLocaleDateString("en-CA");
    localStorage.setItem(`adyap:reminder-dismissed:${today}`, "1");
    setShow(false);
  };

  return (
    <div
      data-testid="reminder-banner"
      className="border-b border-accent/30 bg-gradient-to-r from-accent/10 via-accent/5 to-transparent"
    >
      <div className="max-w-7xl mx-auto px-5 py-2.5 flex items-center gap-3 text-sm">
        <Bell className="w-4 h-4 text-accent shrink-0 animate-pulse" />
        <span className="flex-1 min-w-0">
          <span className="font-medium text-white">Daily nudge</span>
          <span className="text-zinc-400 ml-2">
            You still have <span className="text-white font-medium">{remaining}</span> task{remaining === 1 ? "" : "s"} left today. Keep the streak alive.
          </span>
        </span>
        <Link
          to="/dashboard"
          className="hidden sm:inline-flex items-center gap-1 text-xs bg-accent text-black px-3 py-1.5 rounded-lg font-medium hover:bg-accent/90 transition-colors"
          data-testid="reminder-go-btn"
        >
          Go now <ArrowRight className="w-3.5 h-3.5" />
        </Link>
        <button
          onClick={dismiss}
          className="text-zinc-400 hover:text-white p-1"
          aria-label="Dismiss today's reminder"
          data-testid="reminder-dismiss-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

/** Compact settings block used inside the Settings page. */
export function ReminderSettings() {
  const { user, refreshUser } = useAuth();
  const [enabled, setEnabled] = useState(!!user?.reminder_enabled);
  const [time, setTime] = useState(user?.reminder_time || "19:00");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setEnabled(!!user?.reminder_enabled);
    setTime(user?.reminder_time || "19:00");
  }, [user?.reminder_enabled, user?.reminder_time]);

  const save = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await api.put("/settings/reminder", { enabled, reminder_time: enabled ? time : undefined });
      await refreshUser();
      setSaved(true);
      // clear any previous dismissal so today's reminder can appear if applicable
      const key = `adyap:reminder-dismissed:${new Date().toLocaleDateString("en-CA")}`;
      localStorage.removeItem(key);
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
      setTimeout(() => setSaved(false), 2200);
    }
  };

  return (
    <div className="glass rounded-2xl p-6" data-testid="reminder-settings">
      <div className="flex items-center gap-2 text-zinc-500 text-xs tracking-wider uppercase font-semibold mb-4">
        <Bell className="w-4 h-4 text-accent" /> Daily reminder
      </div>
      <p className="text-sm text-zinc-400 mb-4">
        Pick a time you want a nudge if you haven&apos;t finished today&apos;s tasks. Set it once — Adyap remembers.
      </p>
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm text-zinc-300">Enable daily reminder</span>
        <button
          data-testid="reminder-toggle"
          onClick={() => setEnabled((v) => !v)}
          className={`relative w-11 h-6 rounded-full transition-colors ${enabled ? "bg-accent" : "bg-white/10"}`}
          aria-pressed={enabled}
        >
          <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${enabled ? "translate-x-5" : ""}`} />
        </button>
      </div>
      <label className={`block mb-4 ${enabled ? "" : "opacity-50 pointer-events-none"}`}>
        <span className="text-xs tracking-wider uppercase font-semibold text-zinc-500">Reminder time</span>
        <input
          data-testid="reminder-time-input"
          type="time"
          value={time}
          onChange={(e) => setTime(e.target.value)}
          disabled={!enabled}
          className="mt-1.5 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-accent text-white"
        />
        <span className="text-[11px] text-zinc-500 mt-1 block">Your device&apos;s local time is used.</span>
      </label>
      <button
        data-testid="reminder-save"
        onClick={save}
        disabled={saving}
        className="inline-flex items-center gap-2 bg-accent hover:bg-accent/90 text-black px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-60"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        {saved ? "Saved ✓" : "Save reminder"}
      </button>
    </div>
  );
}
